import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { TopSeller } from '~/types/topSellersTypes';
import fetchWithReauth from '~/redux/baseQueryWithReauth';
import { getTokenDetails } from '~/redux/utils';
import {
  ListingSearchResponseType,
  ListingsResponseType,
} from '~/types/listingTypes';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BASE_URL,
  prepareHeaders: (headers) => {
    const { access_token } = getTokenDetails();
    const locale = location.pathname.split('/')[1];
    if (access_token !== undefined) {
      headers.set('authorization', `Bearer ${access_token}`);
      headers.set(
        'User-Agent',
        `Aqarmap My Account - ${process.env.REACT_APP_APP_VERSION}`,
      );
    }
    headers.set('Accept-Language', locale);
    return headers;
  },
});

export const appApi = createApi({
  reducerPath: 'api',
  baseQuery: fetchWithReauth(baseQuery),
  tagTypes: [
    'user',
    'userPhone',
    'userCards',
    'subscriptionPlans',
    'userSubscription',
    'valuationStats',
    'packages',
    'featuredListingRules',
  ],
  endpoints: () => ({}),
});

export const appApiSlice = appApi.injectEndpoints({
  endpoints: (builder) => ({
    getListings: builder.query<
      ListingSearchResponseType,
      {
        locale: string;
        propertyType: number;
        location: number;
        section: number;
      }
    >({
      query: ({ locale, propertyType, location, section }) => {
        return {
          url: `/api/v4/listings/search?location=${location}&propertyType=${propertyType}&section=${section}&locale=${locale}`,
          headers: {
            'Accept-Language': locale,
          },
        };
      },
    }),
    getTopSellers: builder.query<
      TopSeller[],
      {
        locale: string;
        propertyType: number;
        location: number;
        section: number;
      }
    >({
      query: ({ locale, propertyType, location, section }) => ({
        url: `/api/v4/top-sellers/list?locale=${locale}&propertyType=${propertyType}&location=${location}&section=${section}`,
        method: 'GET',
      }),
    }),
    getRelatedListings: builder.query<
      ListingsResponseType[],
      { locale: string; listingId: string }
    >({
      query: ({ locale, listingId }) => ({
        url: `/api/v2/listing/${listingId}/related-listings`,
        headers: {
          'Accept-Language': locale,
        },
      }),
    }),
    postTopSellerLead: builder.mutation<
      { message: string; created: number },
      { leadId: string; hash: string; sellers: number[] }
    >({
      query: ({ leadId, hash, sellers }) => ({
        url: `/api/v4/leads/${leadId}/top-seller-leads?_hash=${encodeURIComponent(
          hash,
        )}`,
        method: 'POST',
        body: { sellers },
      }),
    }),
    getNavbarLinks: builder.query({
      query: () => '/api/v4/menu/navbar-links',
    }),
  }),
});

export const {
  useGetListingsQuery,
  useGetTopSellersQuery,
  useGetRelatedListingsQuery,
  usePostTopSellerLeadMutation,
  useGetNavbarLinksQuery,
} = appApiSlice;
